import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { GearSix, Users, Clock, Trash, ShieldCheck, Copy, ArrowCounterClockwise, Upload, Palette, Plus, FlowerLotus } from "@phosphor-icons/react";
import { useRestaurant } from "../contexts/RestaurantContext";
import { useAuth } from "../contexts/AuthContext";

export default function SettingsPage() {
  const { restId, activeRestaurant, updateRestaurant } = useRestaurant();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [allAccounts, setAllAccounts] = useState([]);
  const [inviteCode, setInviteCode] = useState("");
  const [logo, setLogo] = useState(null);
  const [theme, setTheme] = useState({ primary_color: "#2F4F4F", accent_color: "#81B29A" });
  const [salesGroups, setSalesGroups] = useState([]);
  const [savingCats, setSavingCats] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get(`/settings`);
      setSettings(data);
    } catch { /* silent */ }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/users");
      setUsers(data);
    } catch { /* silent */ }
  }, []);

  const fetchSalesCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/sales/categories");
      const groups = Array.isArray(data) ? data : data?.groups;
      setSalesGroups(Array.isArray(groups) ? groups : []);
    } catch { /* silent */ }
  }, []);

  const fetchAllAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/all-accounts");
      setAllAccounts(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchSettings(); fetchUsers(); fetchAllAccounts(); fetchSalesCategories(); }, [fetchSettings, fetchUsers, fetchAllAccounts, fetchSalesCategories]);

  // Fetch invite code + restaurant profile
  useEffect(() => {
    api.get("/auth/invite-code").then(({ data }) => setInviteCode(data.invite_code)).catch(() => {});
    if (restId) {
      api.get(`/restaurants/${restId}`).then(({ data }) => {
        if (data.logo) setLogo(data.logo);
        if (data.theme) setTheme(data.theme);
      }).catch(() => {});
    }
  }, [restId]);

  const regenerateInvite = async () => {
    try {
      const { data } = await api.post("/auth/regenerate-invite");
      setInviteCode(data.invite_code);
      toast.success("New invite code generated");
    } catch { toast.error("Failed"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2MB"); return; }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post(`/restaurants/${restId}/logo`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(data.logo);
      toast.success("Logo updated");
    } catch { toast.error("Upload failed"); }
  };

  const saveTheme = async () => {
    try {
      await api.put(`/restaurants/${restId}/theme`, theme);
      toast.success("Theme saved");
    } catch { toast.error("Failed"); }
  };
  // Refresh active status every 30s
  useEffect(() => {
    const interval = setInterval(fetchAllAccounts, 30000);
    return () => clearInterval(interval);
  }, [fetchAllAccounts]);

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.put(`/settings`, settings);
      if (settings.restaurant_name && activeRestaurant) {
        await updateRestaurant(activeRestaurant.id, { name: settings.restaurant_name, address: settings.address });
      }
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const saveSalesCategories = async () => {
    setSavingCats(true);
    try {
      await api.put("/sales/categories", { groups: salesGroups });
      toast.success("Sales categories saved");
    } catch { toast.error("Failed to save categories"); }
    finally { setSavingCats(false); }
  };

  const updateGroupName = (gIdx, name) => {
    const next = [...salesGroups];
    next[gIdx] = { ...next[gIdx], group: name };
    setSalesGroups(next);
  };

  const updateItemLabel = (gIdx, iIdx, label) => {
    const next = [...salesGroups];
    const items = [...(next[gIdx].items || [])];
    items[iIdx] = { ...items[iIdx], label };
    next[gIdx] = { ...next[gIdx], items };
    setSalesGroups(next);
  };

  const addGroup = () => {
    setSalesGroups([...salesGroups, { group: "New Group", color: "#6D6875", items: [] }]);
  };

  const addItemToGroup = (gIdx) => {
    const next = [...salesGroups];
    const items = [...(next[gIdx].items || [])];
    const key = `custom_${Date.now()}`;
    items.push({ key, label: "New Item" });
    next[gIdx] = { ...next[gIdx], items };
    setSalesGroups(next);
  };

  const removeItem = (gIdx, iIdx) => {
    const next = [...salesGroups];
    const items = [...(next[gIdx].items || [])];
    items.splice(iIdx, 1);
    next[gIdx] = { ...next[gIdx], items };
    setSalesGroups(next);
  };

  const removeGroup = (gIdx) => {
    const next = [...salesGroups];
    next.splice(gIdx, 1);
    setSalesGroups(next);
  };

  const deleteUser = async (id) => {
    try {
      await api.delete(`/settings/users/${id}`);
      fetchUsers();
      toast.success("User removed");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const changeRole = async (id, role) => {
    try {
      await api.put(`/settings/users/${id}/role?role=${role}`);
      fetchUsers();
      toast.success("Role updated");
    } catch { toast.error("Failed"); }
  };

  const removeDemo = async () => {
    if (!window.confirm("Remove all sample data (demo tables, members, reservations)? Your real data is untouched.")) return;
    try {
      await api.post("/settings/clear-demo-data");
      toast.success("Sample data removed");
    } catch { toast.error("Failed to clear sample data"); }
  };

  const reseedDemo = async () => {
    if (!window.confirm("Re-add the demo data? Existing demo records will be reset (real data is untouched).")) return;
    try {
      await api.post("/settings/reseed-demo-data");
      toast.success("Sample data restored");
    } catch { toast.error("Failed to reseed sample data"); }
  };

  if (!settings) return <div className="p-6 text-center text-stone-500">Loading settings...</div>;

  const ROLE_COLORS = { admin: "#2F4F4F", host: "#F4A261", server: "#81B29A" };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="settings-page">
      <div className="flex items-center gap-2 mb-6">
        <GearSix size={24} weight="duotone" style={{ color: "#2F4F4F" }} />
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Settings</h2>
        <Badge className="bg-red-100 text-red-700 text-[10px]">Admin Only</Badge>
      </div>

      {/* Restaurant Info */}
      <div className="bg-white rounded-lg border border-stone-200 p-5 mb-5">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4">Restaurant Info</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Name</Label>
            <Input value={settings.restaurant_name || ""} onChange={(e) => setSettings({ ...settings, restaurant_name: e.target.value })} className="mt-1.5" data-testid="settings-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Address</Label>
            <Input value={settings.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })} className="mt-1.5" data-testid="settings-address" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Phone</Label>
            <Input value={settings.phone || ""} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} className="mt-1.5" data-testid="settings-phone" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Timezone</Label>
            <Input value={settings.timezone || ""} onChange={(e) => setSettings({ ...settings, timezone: e.target.value })} className="mt-1.5" data-testid="settings-timezone" />
          </div>
        </div>
      </div>

      {/* Service Hours */}
      <div className="bg-white rounded-lg border border-stone-200 p-5 mb-5">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
          <Clock size={16} weight="duotone" /> Service Hours
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Opening</Label>
            <Input type="time" value={settings.opening_time || "11:00"} onChange={(e) => setSettings({ ...settings, opening_time: e.target.value })} className="mt-1.5" data-testid="settings-opening" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Closing</Label>
            <Input type="time" value={settings.closing_time || "23:00"} onChange={(e) => setSettings({ ...settings, closing_time: e.target.value })} className="mt-1.5" data-testid="settings-closing" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Default Turn (min)</Label>
            <Input type="number" min={15} max={180} value={settings.default_turn_time || 45} onChange={(e) => setSettings({ ...settings, default_turn_time: parseInt(e.target.value) || 45 })} className="mt-1.5" data-testid="settings-turn-time" />
          </div>
        </div>
      </div>

      <Button onClick={saveSettings} disabled={saving} style={{ backgroundColor: "#2F4F4F" }} className="w-full mb-8" data-testid="save-settings-btn">
        {saving ? "Saving..." : "Save Settings"}
      </Button>

      <Separator className="my-6" />

      {/* Restaurant Profile */}
      <div className="bg-white rounded-lg border border-stone-200 p-5 mb-6">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
          <Palette size={16} weight="duotone" /> Restaurant Profile
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Logo */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2 block">Logo</Label>
            <div className="flex items-center gap-4">
              {logo ? (
                <img src={logo} alt="Logo" className="w-16 h-16 rounded-lg object-cover border border-stone-200" data-testid="restaurant-logo" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-stone-100 border border-stone-200 flex items-center justify-center text-stone-400">
                  <Upload size={20} />
                </div>
              )}
              <div>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-upload-input" />
                  <span className="text-xs px-3 py-1.5 rounded-md border border-stone-200 hover:bg-stone-50 transition-colors inline-block">
                    {logo ? "Change Logo" : "Upload Logo"}
                  </span>
                </label>
                <p className="text-[10px] text-stone-400 mt-1">Max 2MB, PNG/JPG</p>
              </div>
            </div>
          </div>
          {/* Color Theme */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2 block">Color Theme</Label>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Label className="text-xs text-stone-600 w-20">Primary</Label>
                <input type="color" value={theme.primary_color} onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="primary-color-input" />
                <span className="text-xs font-mono text-stone-400">{theme.primary_color}</span>
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-xs text-stone-600 w-20">Accent</Label>
                <input type="color" value={theme.accent_color} onChange={(e) => setTheme({ ...theme, accent_color: e.target.value })} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="accent-color-input" />
                <span className="text-xs font-mono text-stone-400">{theme.accent_color}</span>
              </div>
              <Button size="sm" variant="outline" onClick={saveTheme} className="text-xs" data-testid="save-theme-btn">Save Theme</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Sample Data */}
      <div className="bg-white rounded-lg border border-stone-200 p-5 mb-5" data-testid="demo-data-section">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-1 flex items-center gap-2">
          <FlowerLotus size={16} weight="duotone" /> Sample Data
        </h3>
        <p className="text-xs text-stone-500 mb-4 leading-relaxed">When you first signed up, we seeded sample tables, members, and reservations so you could explore. Once you're ready, clear them and start with your own real data.</p>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={removeDemo} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="clear-demo-btn">
            <Trash size={14} className="mr-1.5" /> Clear sample data
          </Button>
          <Button variant="outline" size="sm" onClick={reseedDemo} data-testid="reseed-demo-btn">
            <ArrowCounterClockwise size={14} className="mr-1.5" /> Re-seed sample data
          </Button>
        </div>
      </div>

      {/* Custom Sales Categories */}
      <div className="bg-white rounded-lg border border-stone-200 p-5 mb-6" data-testid="sales-categories-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 flex items-center gap-2">
            <Plus size={16} weight="duotone" /> Custom Sales Categories
          </h3>
          <Button size="sm" variant="outline" onClick={addGroup} data-testid="add-sales-group-btn">
            <Plus size={14} className="mr-1" /> Add Group
          </Button>
        </div>
        <p className="text-xs text-stone-500 mb-4">Customize the sales categories tracked for your restaurant.</p>
        <div className="space-y-4">
          {salesGroups.map((g, gIdx) => (
            <div key={gIdx} className="border border-stone-200 rounded-lg p-3" data-testid={`sales-group-${gIdx}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: g.color || "#6D6875" }} />
                <Input
                  value={g.group || ""}
                  onChange={(e) => updateGroupName(gIdx, e.target.value)}
                  className="text-sm font-semibold flex-1"
                  data-testid={`sales-group-name-${gIdx}`}
                />
                <Button size="sm" variant="ghost" onClick={() => removeGroup(gIdx)} className="text-red-400" data-testid={`remove-sales-group-${gIdx}`}>
                  <Trash size={14} />
                </Button>
              </div>
              <div className="space-y-1.5 ml-5">
                {(g.items || []).map((item, iIdx) => (
                  <div key={iIdx} className="flex items-center gap-2" data-testid={`sales-item-${gIdx}-${iIdx}`}>
                    <Input
                      value={item.label || ""}
                      onChange={(e) => updateItemLabel(gIdx, iIdx, e.target.value)}
                      className="text-xs"
                    />
                    <Button size="sm" variant="ghost" onClick={() => removeItem(gIdx, iIdx)} className="text-red-400 h-7 w-7 p-0">
                      <Trash size={12} />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" onClick={() => addItemToGroup(gIdx)} className="text-xs text-stone-500" data-testid={`add-sales-item-${gIdx}`}>
                  <Plus size={12} className="mr-1" /> Add Item
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Button onClick={saveSalesCategories} disabled={savingCats} style={{ backgroundColor: "#2F4F4F" }} className="w-full mt-4" data-testid="save-sales-categories-btn">
          {savingCats ? "Saving..." : "Save Categories"}
        </Button>
      </div>

      {/* Staff Invite Code */}
      <div className="bg-white rounded-lg border border-stone-200 p-5 mb-6">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2">
          <ShieldCheck size={16} weight="duotone" /> Staff Invite Code
        </h3>
        <p className="text-xs text-stone-500 mb-3">Share this code with hosts and servers so they can join your restaurant.</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-stone-50 rounded-lg border border-stone-200 px-4 py-3 font-mono text-lg tracking-[0.3em] font-bold text-center" style={{ color: "#2F4F4F" }} data-testid="invite-code-display">
            {inviteCode || "Loading..."}
          </div>
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(inviteCode); toast.success("Copied!"); }} data-testid="copy-invite-btn">
            <Copy size={16} />
          </Button>
          <Button variant="outline" size="sm" onClick={regenerateInvite} data-testid="regen-invite-btn">
            <ArrowCounterClockwise size={16} />
          </Button>
        </div>
      </div>

      {/* User Management */}
      <div className="bg-white rounded-lg border border-stone-200 p-5">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
          <Users size={16} weight="duotone" /> Staff Accounts
          {allAccounts.filter((a) => a.is_online).length > 0 && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              {allAccounts.filter((a) => a.is_online).length} online
            </span>
          )}
        </h3>
        <div className="space-y-2">
          {users.map((u) => {
            const account = allAccounts.find((a) => a.id === u.id);
            const isOnline = account?.is_online || false;
            return (
            <div key={u.id} className="flex items-center justify-between p-3 rounded-lg border border-stone-100 hover:bg-stone-50 transition-colors" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: ROLE_COLORS[u.role] || "#9CA3AF" }}>
                    {u.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${isOnline ? "bg-green-500" : "bg-stone-300"}`} data-testid={`online-status-${u.id}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-800">{u.name}</p>
                  <p className="text-[10px] text-stone-500">{u.email}{isOnline ? " · Online" : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={u.role}
                  onChange={(e) => changeRole(u.id, e.target.value)}
                  className="text-xs border border-stone-200 rounded-md px-2 py-1 bg-white"
                  data-testid={`role-select-${u.id}`}
                >
                  <option value="admin">Admin</option>
                  <option value="host">Host</option>
                  <option value="server">Server</option>
                </select>
                {u.id !== user?.id && (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => deleteUser(u.id)} data-testid={`delete-user-${u.id}`}>
                    <Trash size={14} />
                  </Button>
                )}
                {u.id === user?.id && <ShieldCheck size={16} className="text-[#2F4F4F]" weight="fill" />}
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
